import { useTranslation } from 'react-i18next'

import { useGetSurveyAnalytics } from '@/api/communication/surveys/get-survey-analytics'
import { Typography } from '@/components/ui/typography'
import { SurveyCommunicationType } from '@/models'
import { toFixed } from '@/utils/math'

import * as S from './survey-details-analytics.styled'
import { isEmailType, isInAppType } from './survey-details-analytics.utils'

interface SurveyDetailsAnalyticsProps {
  id: number
  communicationType: SurveyCommunicationType
}

export const SurveyDetailsAnalytics = ({ id, communicationType }: SurveyDetailsAnalyticsProps) => {
  const { t } = useTranslation(['communication'])
  const { data: analytics, isLoading } = useGetSurveyAnalytics({ id })

  const showTargeted = !isInAppType(communicationType)
  const showEmailMetrics = isEmailType(communicationType)

  return (
    <S.StyledCard $isLoading={isLoading}>
      {showTargeted && (
        <S.MetricCard>
          <Typography size="text-small" weight="semibold" color="black800">
            {t('communication:surveyDetails.analytics.totalTargeted')}
          </Typography>
          <Typography size="heading-huge" color="black800">
            {analytics?.target ?? 0}
          </Typography>
        </S.MetricCard>
      )}

      <S.MetricCard>
        <Typography size="text-small" weight="semibold" color="black800">
          {t('communication:surveyDetails.analytics.totalSent')}
        </Typography>
        <Typography size="heading-huge" color="black800">
          {analytics?.sent ?? 0}
        </Typography>
      </S.MetricCard>

      {showEmailMetrics && (
        <>
          <S.MetricCard>
            <Typography size="text-small" weight="semibold" color="black800">
              {t('communication:surveyDetails.analytics.deliveryRate')}
            </Typography>
            <S.ValueWithUnit>
              <Typography as="span" color="black800" size="heading-huge">
                {toFixed(analytics?.deliveryRate)}
              </Typography>
              <Typography as="span" color="black800" size="text-small" weight="semibold">
                %
              </Typography>
            </S.ValueWithUnit>
          </S.MetricCard>

          <S.MetricCard>
            <Typography size="text-small" weight="semibold" color="black800">
              {t('communication:surveyDetails.analytics.openRate')}
            </Typography>
            <S.ValueWithUnit>
              <Typography as="span" color="black800" size="heading-huge">
                {toFixed(analytics?.openRate)}
              </Typography>
              <Typography as="span" color="black800" size="text-small" weight="semibold">
                %
              </Typography>
            </S.ValueWithUnit>
          </S.MetricCard>
        </>
      )}

      <S.MetricCard>
        <Typography size="text-small" weight="semibold" color="black800">
          {t('communication:surveyDetails.analytics.replyRate')}
        </Typography>
        <S.ValueWithUnit>
          <Typography as="span" color="black800" size="heading-huge">
            {toFixed(analytics?.responseRate)}
          </Typography>
          <Typography as="span" color="black800" size="text-small" weight="semibold">
            %
          </Typography>
        </S.ValueWithUnit>
      </S.MetricCard>
    </S.StyledCard>
  )
}
